import type { PublicLocale } from './locale';

export type AnalyticsConsent = 'unknown' | 'granted' | 'denied';

type GtagCommand = [command: string, ...args: unknown[]];

declare global {
  interface Window {
    dataLayer?: GtagCommand[];
    gtag?: (...args: GtagCommand) => void;
  }
}

const CONSENT_STORAGE_KEY = 'parknuto.analyticsConsent.v1';
const measurementId = (import.meta.env.VITE_GA_MEASUREMENT_ID ?? '').trim();
let scriptPromise: Promise<void> | null = null;

export function isValidGa4MeasurementId(value: string): boolean {
  return /^G-[A-Z0-9]+$/.test(value);
}

export function isAnalyticsConfigured(): boolean {
  return isValidGa4MeasurementId(measurementId);
}

export function getAnalyticsConsent(): AnalyticsConsent {
  if (typeof window === 'undefined') return 'unknown';
  const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  return stored === 'granted' || stored === 'denied' ? stored : 'unknown';
}

function gtag(...args: GtagCommand): void {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(args);
}

function setGoogleConsent(granted: boolean): void {
  gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
}

async function loadGoogleAnalytics(): Promise<void> {
  if (!isAnalyticsConfigured()) return;
  if (scriptPromise) return scriptPromise;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = (...args: GtagCommand) => gtag(...args);
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  gtag('set', 'allow_google_signals', false);
  gtag('set', 'ads_data_redaction', true);

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google Analytics failed to load'));
    document.head.appendChild(script);
  });

  try {
    await scriptPromise;
    if (getAnalyticsConsent() !== 'granted') {
      setGoogleConsent(false);
      return;
    }
    gtag('js', new Date());
    setGoogleConsent(true);
    gtag('config', measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
  } catch {
    scriptPromise = null;
  }
}

export function initializeAnalytics(): void {
  if (getAnalyticsConsent() === 'granted') {
    void loadGoogleAnalytics();
  }
}

export function setAnalyticsConsent(
  consent: Exclude<AnalyticsConsent, 'unknown'>,
): void {
  window.localStorage.setItem(CONSENT_STORAGE_KEY, consent);
  if (consent === 'granted') {
    void loadGoogleAnalytics();
  } else if (window.dataLayer) {
    setGoogleConsent(false);
  }
}

export function trackPageView(path: string, title: string): void {
  if (getAnalyticsConsent() !== 'granted' || !isAnalyticsConfigured()) return;
  void loadGoogleAnalytics().then(() => {
    if (!scriptPromise || getAnalyticsConsent() !== 'granted') return;
    gtag('event', 'page_view', {
      page_location: `${window.location.origin}${path}`,
      page_path: path,
      page_title: title,
    });
  });
}

// Clicks are intent signals, never registrations or confirmed leads.
export function trackPublicAction(
  action: 'official_payment_link_clicked' | 'launch_interest_clicked',
  locale: PublicLocale,
): void {
  if (getAnalyticsConsent() !== 'granted' || !isAnalyticsConfigured()) return;
  void loadGoogleAnalytics().then(() => {
    if (!scriptPromise || getAnalyticsConsent() !== 'granted') return;
    gtag('event', action, { locale });
  });
}
